import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('nnc_lang');
    return saved === 'en' ? 'en' : 'da';
  });

  const setLanguage = (lang) => {
    const validLang = lang === 'en' ? 'en' : 'da';
    setLanguageState(validLang);
    localStorage.setItem('nnc_lang', validLang);
  };

  // UI Translations
  const uiTranslations = {
    da: {
      home: 'Forside',
      incorporation: 'Stiftelse af selskaber',
      properties: 'Køb og salg af ejendomme',
      companies: 'Køb og salg af virksomheder i ind- og udland',
      investments: 'Investeringer i virksomheder',
      contact: 'Kontakt os',
      quickLinks: 'Hurtige links',
      contactTitle: 'Kontakt',
      rightsReserved: 'Alle rettigheder forbeholdes.',
      cvrNotDisclosed: 'CVR: Ej oplyst',
      pageNotFound: 'Siden blev ikke fundet',
      pageNotFoundDesc: 'Vi kunne desværre ikke finde den side, du leder efter. Kontroller venligst URL\'en eller gå tilbage til forsiden.',
      goToHome: 'Gå til forside',
      accessDenied: 'Adgang nægtet',
      accessDeniedDesc: 'Du har ikke tilladelse til at se denne side.',
      welcomeTitle: 'Velkommen Til NNC Revision Ltd.',
      newsCurrent: 'Nyhed AKTUELT',
      tagline: 'Vi bygger bro til hinanden',
      welcomeText1: 'NNC Revision Ltd. tilbyder specialiseret rådgivning inden for stiftelse af selskaber, investeringer samt køb og salg af ejendomme og virksomheder i både ind- og udland. Vi sikrer en professionel, rolig og seriøs behandling af alle sager med fokus på det internationale og amerikanske marked.',
      welcomeText2: 'NNC Revision Ltd. er din pålidelige partner, når det gælder komplekse selskabsstrukturer, ejendomshandler og virksomhedsoverdragelser på tværs af landegrænser.',
    },
    en: {
      home: 'Home',
      incorporation: 'Establishing of companies',
      properties: 'Purchase and sale of properties',
      companies: 'Purchase & sales of companies at home and abroad',
      investments: 'Investments in companies',
      contact: 'Contact us',
      quickLinks: 'Quick links',
      contactTitle: 'Contact',
      rightsReserved: 'All rights reserved.',
      cvrNotDisclosed: 'CVR: Not disclosed',
      pageNotFound: 'Page not found',
      pageNotFoundDesc: 'We could not find the page you are looking for. Please check the URL or go back to the homepage.',
      goToHome: 'Go to homepage',
      accessDenied: 'Access denied',
      accessDeniedDesc: 'You do not have permission to view this page.',
      welcomeTitle: 'Welcome To NNC Revision Ltd.',
      newsCurrent: 'News CURRENT',
      tagline: 'We build bridges to each other',
      welcomeText1: 'NNC Revision Ltd. offers specialized advice within incorporation of companies, investments, as well as purchase and sale of properties and companies both domestically and abroad. We ensure a professional, calm, and serious handling of all cases with a focus on the international and American market.',
      welcomeText2: 'NNC Revision Ltd. is your reliable partner when it comes to complex corporate structures, property transactions, and company transfers across borders.',
    }
  };

  // Page Content Translations
  const contentTranslations = {
    'stiftelse-af-selskaber': {
      title: 'Establishing of companies',
      meta_title: 'Establishing of companies - NNC Revision Ltd.',
      meta_description: 'Professional advice on incorporation of companies at NNC Revision Ltd. and establishment of company structures.',
      content: `
        <p><strong>Formation of Companies</strong></p>
        <p>New and dormant companies</p>
        <p>Formation of English companies free of charge</p>
        <p>&nbsp;</p>
        <p>We can help you raise the share capital.</p>
        <p>&nbsp;</p>
        <p>Consultancy in connection with start-up of new companies.</p>
        <p>We specialise in reconstructing companies which are close to bankrupcy. Come before it is too late.</p>
        <p>We prepare various company structures (domestic or abroad). We can also provide public limited companies and funds.</p>
        <p>Consultancy within different company structures.</p>
        <p>REMEMBER! The cheapest life insurance you can give your family is a limited company if you consider establishing your own business.</p>
        <p>We can help you obtaining loans if you can provide the necessary collateral.</p>
        <p>Should you have a bad credit record we can still help you obtaining a loan.</p>
      `
    },
    'koeb-og-salg-af-ejendomme': {
      title: 'Purchase and sale of properties',
      meta_title: 'Purchase and sale of properties - NNC Revision Ltd.',
      meta_description: 'Get professional advice and assistance with the purchase and sale of properties at NNC Revision Ltd.',
      content: `
        <p>We facilitate the purchase and sale of properties to investors domestically and abroad, including in Bangkok.</p>
        <p>See under contact us.</p>
      `
    },
    'koeb-og-salg-af-virksomheder-i-ind-og-udland': {
      title: 'Purchase & sales of companies at home and abroad',
      meta_title: 'Purchase & sales of companies at home and abroad - NNC Revision Ltd.',
      meta_description: 'Professional advice on buying and selling companies domestically and abroad at NNC Revision Ltd.',
      content: `
        <p>You can contact us if you are interested in buying or selling companies.</p>
        <p>See under contact us.</p>
      `
    },
    'investeringer-i-virksomheder': {
      title: 'Investments in companies',
      meta_title: 'Investments in companies - NNC Revision Ltd.',
      meta_description: 'Professional advice on investments in companies at NNC Revision Ltd.',
      content: `
        <p>Investment in new and used companies, including manufacturing companies, trading companies, shops, and ship investments and related businesses.</p>
        <p>See under contact us.</p>
      `
    },
    'kontakt-os': {
      title: 'Contact us',
      meta_title: 'Contact us - NNC Revision Ltd.',
      meta_description: 'Contact NNC Revision Ltd. for professional advice and auditing. See our contact details here.',
      content: `
        <p>Here you can contact us:</p>
        <p>&nbsp;</p>
        <p><strong>NNC Revision Ltd.</strong><br />FLAT 3 Repton Court,<br />1A Repton Avenue<br />Ashford TN23 3TL.<br />UK<br />c/o Nørrebrogade 40<br />7100 Vejle</p>
        <p>&nbsp;</p>
        <p><strong>Tel.:</strong> &nbsp;&nbsp;<a href="tel:+4575722337" class="whitespace-nowrap">+45 75 72 23 37</a></p>
        <p><strong>Mobile:</strong> &nbsp;&nbsp;<a href="tel:+4540731267" class="whitespace-nowrap">+45 40 73 12 67</a></p>
        <p><strong>E-mail:</strong> &nbsp;<a href="mailto:nncrevision@nncrevision.dk" class="whitespace-nowrap">nncrevision@nncrevision.dk</a></p>
      `
    },
    'index': {
      title: 'NNC Revision Ltd.',
      meta_title: 'NNC Revision Ltd.',
      meta_description: 'NNC Revision Ltd. offers professional advice on incorporation of companies, purchase and sale of properties and companies domestically and abroad, as well as investments.',
    }
  };

  const t = (key) => {
    return uiTranslations[language][key] || key;
  };

  const getTranslatedContent = (slug) => {
    if (language === 'da') return null;
    return contentTranslations[slug] || null;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getTranslatedContent }}>
      {children}
    </LanguageContext.Provider>
  );
};
